import {
    Box,
    Grid,
    IconButton,
    SelectChangeEvent,
    Skeleton,
    Theme,
    Typography
} from "@mui/material";
import { useState } from "react";
import CircleIcon from '@mui/icons-material/Circle';
import { calculateOthersInPieChart } from "../../../utils/utilityFunctions";
import ListModel from "../../dashboard/chart/ListModel";
import { BlueRightIcon } from "../../../assets/SvgIcons";
import { ErrorText } from "../../dashboard/chart/CustomeLineChart";
import { filterType } from "../../../redux-store/types/api-types";
import { DropdownOption } from "../directEquity/types";
import CustomePieChart from "../../dashboard/chart/CustomePieChart";
import { PieChartComponentLoading } from "../directEquity/LoadingComponents";

const pieChartColor = ["#00A76F", "#FFAB00", "#3C92DC", "#3CA9DC", "#FF5630", "#0D3C6B"]


interface PieChartComponentProps {
    isLoading: boolean;
    pieChartData: (string | number)[][];
    pieChartLabelData: filterType[];
    pieChartTotalValue: filterType;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    title?: string
    apiError?: boolean
}

export default function FamilyPieChartComponent({
    isLoading,
    pieChartData,
    pieChartLabelData,
    pieChartTotalValue,
    open,
    setOpen,
    title,
    apiError = false,
}: PieChartComponentProps): JSX.Element {
    const modifiedData = calculateOthersInPieChart({ labelData: pieChartLabelData, totalValue: pieChartTotalValue, data: pieChartData })
    return (
        <>
            {isLoading && <Loading title={title} />}


            {(!isLoading) && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Typography
                            variant="h2"
                            sx={(theme: Theme) => ({
                                lineHeight: "25px",
                                letterSpacing: "0.01px",
                                color: theme.palette.text.primary
                            })}
                        >
                            {title || "Asset Allocation"}
                        </Typography>
                    </Box>
                    {apiError ? <Typography variant="body_600">Something went wrong</Typography> :


                        (
                            (pieChartData?.length || 0) === 0 ||
                            (pieChartLabelData?.length || 0) === 0 ||
                            (Object.keys(pieChartTotalValue)?.length || 0) === 0
                        ) ?
                            <ErrorText /> :
                            <CustomePieChart
                                data={modifiedData?.data}
                                isLoading={false}
                                labelData={modifiedData?.labelData}
                                totalValue={pieChartTotalValue}
                                showSlider={false}
                                align='center'
                                title={title || "Asset Allocation"}
                            />
                    }
                    <Grid
                        container
                        sx={{ paddingRight: "10px" }}
                        rowSpacing={3}
                    >
                        {pieChartLabelData?.length < 6 ? [...pieChartLabelData].map(({ label, value }: filterType, index: number) => (
                            <>
                                <Grid
                                    key={label}
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                        columnGap: "15px",
                                    }}
                                >
                                    <CircleIcon
                                        style={{
                                            color: pieChartColor[index],
                                            width: '11px',
                                            height: '11px',
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle_500"
                                        sx={(theme: Theme) => ({
                                            // lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {label}
                                    </Typography>
                                </Grid>
                                <Grid
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        columnGap: "15px"
                                    }}
                                >
                                    <Typography
                                        variant="subtitle_500"
                                        sx={(theme: Theme) => ({
                                            // lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {!Number.isNaN(Number(value ?? 0)) ?
                                            `${Math.round((Number(value ?? 0)) * 100) / 100}%` : '0%'}
                                    </Typography>
                                </Grid>
                            </>
                        )) : [...modifiedData?.labelData].slice(0, 6).map(({ label, value }: filterType, index: number) => (
                            <>
                                <Grid
                                    key={label}
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                        columnGap: "15px",
                                    }}
                                >
                                    <CircleIcon
                                        style={{
                                            color: 5 === index ? "transparent" : pieChartColor[index],
                                            width: '11px',
                                            height: '11px',
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle_400"
                                        sx={(theme: Theme) => ({
                                            lineHeight: "20px",
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {index === 5 ? 'Show More' : label}
                                    </Typography>
                                </Grid>
                                <Grid
                                    xs={6}
                                    item
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        alignItems: "center",
                                        columnGap: "15px"
                                    }}
                                >
                                    {index === 5 ? <IconButton onClick={() => setOpen(true)}>
                                        <BlueRightIcon />
                                    </IconButton> :
                                        <Typography
                                            variant="subtitle_400"
                                            sx={(theme: Theme) => ({
                                                lineHeight: "20px",
                                                color: theme.palette.text.primary
                                            })}
                                        >
                                            {!Number.isNaN(Number(value ?? 0)) ?
                                                `${Math.round((Number(value ?? 0)) * 100) / 100}%` : '0%'}
                                        </Typography>
                                    }
                                </Grid>
                            </>
                        ))}
                    </Grid>
                </>
            )}
            <ListModel open={open} handleClose={() => setOpen(false)} labelData={[...(pieChartLabelData || [])]} title={title || "Asset Allocation"} />
        </>
    )
}

export const Loading = ({ title }: { title: string | undefined }): JSX.Element => {
    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                }}
            >
                <Typography
                    variant="h2"
                    sx={(theme: Theme) => ({
                        lineHeight: "25px",
                        letterSpacing: "0.01px",
                        color: theme.palette.text.primary
                    })}
                >
                    {title || "Asset Allocation"}
                </Typography>

            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
            }}>
                <Skeleton
                    variant="circular"
                    height={200}
                    sx={{
                        margin: "35px",
                        width: '100%',
                        maxWidth: '200px',
                    }} />
            </Box>
            <Grid
                container
                sx={{ paddingRight: "10px" }}
                rowSpacing={3}
            >
                {[...Array(5)].map((_, index) => (
                    <>
                        <Grid
                            key={index}
                            xs={6}
                            item
                            sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                columnGap: "15px",
                            }}
                        >
                            <Skeleton
                                variant="circular"
                                width={11}
                                height={11}
                                animation="wave"
                            />
                            <Skeleton
                                sx={{ fontSize: '16px' }}
                                animation="wave"
                                width={100}
                            />
                        </Grid>
                        <Grid
                            xs={6}
                            item
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                columnGap: "15px"
                            }}
                        >
                            <Skeleton
                                sx={{ fontSize: '16px' }}
                                animation="wave"
                                width={100}
                            />
                        </Grid>
                    </>
                ))}
            </Grid>
        </>
    )
}